import React, { useEffect, useRef, useState, Children } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Brain, Heart, Shield, TrendingUp, MessageSquare, Lock, Zap, CheckCircle2, User, MessageCircle, Crown, ArrowRight, Wand2 } from 'lucide-react';
import { WebsiteHeader } from '../components/Dashboard/WebsiteHeader';
import { SubscriptionModal } from '../components/Settings/SubscriptionModal';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { trackEvent, trackCtaClick } from '../utils/visitorTracking';
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 50,
      damping: 15
    }
  }
};
export function PremiumToolsPage() {
  const {
    user
  } = useAuth();
  const navigate = useNavigate();
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  // Reliable Pro status check
  const [profile, setProfile] = useState<{
    is_pro: boolean;
    scan_credits: number;
  } | null>(null);
  const [loadingProStatus, setLoadingProStatus] = useState(true);
  const isPro = profile?.is_pro || false;
  // Ref guard so React Strict Mode's double-invoked effect logs one view
  const premiumViewTracked = useRef(false);
  useEffect(() => {
    if (premiumViewTracked.current) return;
    premiumViewTracked.current = true;
    void trackEvent('premium_view', {
      location: 'premium_tools_page'
    });
  }, []);
  useEffect(() => {
    async function fetchProfile() {
      if (!user?.id) return;
      try {
        const {
          data,
          error
        } = await supabase.from('profiles').select('is_pro, scan_credits').eq('id', user.id).single();
        if (error) throw error;
        setProfile(data);
      } catch (error) {
        console.error('Error fetching profile:', error);
        setProfile(null);
      } finally {
        setLoadingProStatus(false);
      }
    }
    fetchProfile();
    // Real-time subscription for profile updates
    const channel = supabase.channel('profile-tools-updates').on('postgres_changes', {
      event: 'UPDATE',
      schema: 'public',
      table: 'profiles',
      filter: `id=eq.${user?.id}`
    }, payload => {
      const newProfile = payload.new as {
        is_pro: boolean;
        scan_credits: number;
      };
      setProfile(newProfile);
    }).subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);
  const tools = [{
    icon: Wand2,
    title: 'Message Rewriter',
    description: 'Transform emotionally charged messages into healthy, effective communication with AI-powered tone coaching.',
    status: 'Free · 3/day',
    theme: 'green',
    code: 'MSG_RW_01',
    route: '/tools/rewriter',
    freeAccess: true
  }, {
    icon: Brain,
    title: 'Advanced Pattern Detection',
    description: 'AI-powered analysis to identify subtle manipulation tactics and gaslighting patterns in your conversations.',
    status: 'Pro Only',
    theme: 'red',
    code: 'MOD_AI_01',
    route: '/upload'
  }, {
    icon: Heart,
    title: 'Relationship Health Score',
    description: 'Get a comprehensive score of your relationship dynamics based on communication patterns and emotional safety.',
    status: 'Pro Only',
    theme: 'red',
    code: 'REL_SCORE_V2',
    route: '/dashboard'
  }, {
    icon: MessageSquare,
    title: 'Conversation Insights',
    description: 'Deep dive into communication styles, response times, and hidden meanings behind the texts.',
    status: 'Pro Only',
    theme: 'red',
    code: 'TXT_DECODE',
    route: '/all-scans'
  }, {
    icon: User,
    title: 'ToxicPersona AI',
    description: 'Simulate conversations with specific toxic personality types to practice setting boundaries safely.',
    status: 'Gold Tier',
    subStatus: 'Coming Soon',
    theme: 'gold',
    code: 'SIM_ENGINE',
    route: null
  }, {
    icon: MessageCircle,
    title: 'Chat Analyzer',
    description: 'Real-time analysis of ongoing chats to detect passive-aggression and manipulation as it happens.',
    status: 'Gold Tier',
    subStatus: 'Coming Soon',
    theme: 'gold',
    code: 'RT_MONITOR',
    route: null
  }, {
    icon: TrendingUp,
    title: 'Emotional Tone Analysis',
    description: 'Track emotional shifts throughout conversations to understand triggers and mood patterns.',
    status: 'Coming Soon',
    theme: 'gray',
    code: 'EMO_TRACK',
    route: null
  }, {
    icon: Shield,
    title: 'Manipulation Shield',
    description: 'Real-time alerts for potential manipulation attempts and suggestions for healthy boundaries.',
    status: 'Coming Soon',
    theme: 'gray',
    code: 'DEF_SYS_01',
    route: null
  }, {
    icon: Lock,
    title: 'Trust Metrics',
    description: 'Scientific evaluation of consistency and honesty markers in your digital interactions.',
    status: 'Coming Soon',
    theme: 'gray',
    code: 'TRU_METRIC',
    route: null
  }];
  const benefits = ['Unlimited conversation analysis', 'Priority processing speed', 'Detailed PDF reports', 'Historical trend tracking', 'Expert-backed recommendations'];
  const getThemeStyles = (theme: string) => {
    switch (theme) {
      case 'red':
        return {
          iconBg: 'bg-[#FF0000]/10 text-[#FF0000] border border-[#FF0000]/30',
          badge: 'bg-[#FF0000]/10 text-[#FF0000] border border-[#FF0000]/30',
          border: 'border-gray-800 hover:border-[#FF0000]',
          hoverShadow: 'hover:shadow-[0_0_20px_rgba(255,0,0,0.1)]'
        };
      case 'green':
        return {
          iconBg: 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30',
          badge: 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30',
          border: 'border-gray-800 hover:border-emerald-500',
          hoverShadow: 'hover:shadow-[0_0_20px_rgba(16,185,129,0.1)]'
        };
      case 'gold':
        return {
          iconBg: 'bg-[#FFD700]/10 text-[#FFD700] border border-[#FFD700]/30',
          badge: 'bg-[#FFD700]/10 text-[#FFD700] border border-[#FFD700]/30',
          border: 'border-gray-800 hover:border-[#FFD700]',
          hoverShadow: 'hover:shadow-[0_0_20px_rgba(255,215,0,0.1)]'
        };
      default:
        // gray
        return {
          iconBg: 'bg-gray-800 text-gray-400 border border-gray-700',
          badge: 'bg-gray-800 text-gray-400 border border-gray-700',
          border: 'border-gray-800 hover:border-gray-600',
          hoverShadow: 'hover:shadow-none'
        };
    }
  };
  const handleToolClick = (route: string | null, freeAccess?: boolean) => {
    if (!route) return;
    if (isPro || freeAccess) {
      trackCtaClick('Premium Tool', 'premium_tools_page', route);
      navigate(route);
    } else {
      trackCtaClick('Upgrade', 'premium_tools_locked_tool', route);
      void trackEvent('premium_checkout_started', {
        location: 'premium_tools_locked_tool',
        tool_route: route
      });
      setShowSubscriptionModal(true);
    }
  };
  return <div className="flex flex-col w-full min-h-screen bg-[#050505] relative overflow-hidden" data-id="element-9289" data-mp="2tpu4d289is96">
      {/* Grid Background */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(20,20,20,0.5)_1px,transparent_1px),linear-gradient(90deg,rgba(20,20,20,0.5)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,#000_70%,transparent_100%)] pointer-events-none fixed" data-id="element-9290" data-mp="25zrfsw3k44fb" />

      <WebsiteHeader data-id="element-9291" data-mp="2vu4mf1g0baav" />

      <div className="max-w-7xl mx-auto w-full relative z-10" data-id="element-9292" data-mp="2vvp7m0gflgw2">
        <main className="px-6 pt-12 pb-24" data-id="element-9293" data-mp="2zuessf7po2q9">
          {/* Hero Section */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="text-center mb-16 max-w-3xl mx-auto" data-id="element-9294" data-mp="34uss98v8y4co">
            <div className="inline-flex items-center justify-center px-4 py-1.5 bg-[#FFD700]/10 border border-[#FFD700]/30 mb-6 shadow-[0_0_15px_rgba(255,215,0,0.1)]" data-id="element-9295" data-mp="3jjp5rlg2o9ko">
              <Sparkles className="w-4 h-4 text-[#FFD700] mr-2" data-id="element-9296" data-mp="3skn2rjs3fgtr" />
              <span className="text-xs font-bold text-[#FFD700] uppercase tracking-widest font-mono" data-id="element-9297" data-mp="1dk9mmsp30gw">
                Premium Features
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-black text-white mb-6 tracking-tight leading-tight uppercase" data-id="element-9298" data-mp="bisnexn8ure5">
              Advanced Tools for <br data-id="element-9299" data-mp="1j1p0kvkbs8s" />
              <span className="text-[#FF0000]" data-id="element-9300" data-mp="1ijlr4ahgdrd">Deeper Insights</span>
            </h1>
            <p className="text-lg text-gray-400 leading-relaxed max-w-2xl mx-auto font-mono" data-id="element-9301" data-mp="3tndrz9jim30u">
              Unlock the full potential of our AI analysis to better understand
              your relationships and protect your emotional well-being with
              professional-grade tools.
            </p>
          </motion.div>

          {/* Tools Grid */}
          <motion.div variants={containerVariants} initial="hidden" animate="visible" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20" data-id="element-9302" data-mp="kgcl5wlwe5k0">
            {tools.map((tool, index) => {
            const Icon = tool.icon;
            const styles = getThemeStyles(tool.theme);
            const isComingSoon = tool.status.includes('Coming Soon') || tool.subStatus?.includes('Coming Soon');
            const isClickable = !isComingSoon && !loadingProStatus;
            return <motion.div key={index} variants={itemVariants} whileHover={isClickable ? {
              y: -4
            } : {}} onClick={() => isClickable && handleToolClick(tool.route, tool.freeAccess)} className={`group relative bg-[#0a0a0a] p-8 border ${styles.border} ${isClickable ? styles.hoverShadow : ''} transition-all duration-300 overflow-hidden ${isClickable ? 'cursor-pointer' : 'opacity-80'}`} data-id="element-9303" data-mp="1csc08t88ydu2" data-mpi={index}>
                  {/* Corner Brackets */}
                  <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-9304" data-mp="20eihm63k95wy" />
                  <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-9305" data-mp="2p1rl3icpv1p9" />
                  <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-9306" data-mp="2a6bj8r5wa1ib" />
                  <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-9307" data-mp="21bnd6yshwfsg" />

                  {/* Scanning Line on Hover */}
                  {isClickable && <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-[#FF0000] to-transparent opacity-0 group-hover:opacity-50 group-hover:animate-[scan_2s_ease-in-out_infinite]" data-id="element-9308" data-mp="1rfkc7w9dqn73" />}

                  <div className="flex justify-between items-start mb-6" data-id="element-9309" data-mp="1mf664jrc9nbp">
                    <div className={`p-3 ${styles.iconBg} transition-colors duration-300`} data-id="element-9310" data-mp="1mghbup4ch55g">
                      <Icon size={24} strokeWidth={2} data-id="element-9311" data-mp="3kabjjvt7n6cm" />
                    </div>
                    <div className="flex flex-col items-end" data-id="element-9312" data-mp="3fb8pf6erqkjf">
                      <span className={`text-[10px] font-bold px-2 py-1 uppercase tracking-widest font-mono ${styles.badge}`} data-id="element-9313" data-mp="3f9fidq08tyaw">
                        {tool.status}
                      </span>
                      {tool.subStatus && <span className="text-[10px] text-gray-500 font-mono mt-1" data-id="element-9314" data-mp="6w8brhg9zef8">
                          {tool.subStatus}
                        </span>}
                    </div>
                  </div>

                  <div className="mb-2 flex items-center justify-between" data-id="element-9315" data-mp="3u2r676fgcj3u">
                    <h3 className={`text-lg font-bold text-white uppercase tracking-wide ${isClickable ? 'group-hover:text-[#FF0000]' : ''} transition-colors`} data-id="element-9316" data-mp="2mo5wctsv320k">
                      {tool.title}
                    </h3>
                  </div>

                  <div className="text-[10px] text-gray-600 font-mono mb-4 uppercase tracking-widest" data-id="element-9317" data-mp="125bs5bmf67gm">
                    Code: {tool.code}
                  </div>

                  <p className="text-gray-400 text-sm leading-relaxed mb-6 font-mono" data-id="element-9318" data-mp="1buvcymtp0be4">
                    {tool.description}
                  </p>

                  {!isComingSoon && !loadingProStatus && <div className="pt-4 border-t border-gray-800 flex items-center justify-between w-full" data-id="element-9319" data-mp="1lrqbh5pyubse">
                      {isPro || tool.freeAccess ? <span className={`flex items-center ${tool.freeAccess && !isPro ? 'text-emerald-400' : 'text-[#FF0000]'} text-xs font-bold uppercase tracking-widest transition-colors`} data-id="element-9320" data-mp="1uthiqf05mqod">
                          {tool.freeAccess && !isPro ? 'Try Free' : 'Access Tool'}
                          <ArrowRight size={14} className="ml-2 group-hover:translate-x-1 transition-transform" data-id="element-9321" data-mp="31ch8a37wb2kq" />
                        </span> : <span className="flex items-center text-gray-400 text-xs font-bold uppercase tracking-widest group-hover:text-[#FF0000] transition-colors" data-id="element-9322" data-mp="36asdu1r5qqx5">
                          <Lock size={14} className="mr-2 text-gray-500 group-hover:text-[#FF0000] transition-colors" data-id="element-9323" data-mp="2dlbstp5vacay" />
                          Unlock Access
                        </span>}
                    </div>}
                </motion.div>;
          })}
          </motion.div>

          {/* Benefits & CTA Section */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.98
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="relative bg-[#0a0a0a] border border-[#FF0000]/30 p-8 md:p-12 overflow-hidden" data-id="element-9324" data-mp="1uubxm67s4698">
            {/* Corner Brackets (Thick) */}
            <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-[#FF0000]" data-id="element-9325" data-mp="t3u5w3lpftd5" />
            <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-[#FF0000]" data-id="element-9326" data-mp="y1chy2v0qopd" />
            <div className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-[#FF0000]" data-id="element-9327" data-mp="k234vc240apm" />
            <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-[#FF0000]" data-id="element-9328" data-mp="o612fibsddgk" />

            {/* Scanning Line */}
            <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#FF0000] to-transparent animate-[scan_4s_ease-in-out_infinite]" data-id="element-9329" data-mp="38uorsluq8a80" />

            <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-12" data-id="element-9330" data-mp="1fdar3tppqctx">
              <div className="flex-1 max-w-2xl" data-id="element-9331" data-mp="15ln4c643pjys">
                <div className="flex items-center gap-2 mb-4" data-id="element-9332" data-mp="15kc0k6anoc7o">
                  <Crown className="w-5 h-5 text-[#FFD700]" data-id="element-9333" data-mp="10mtmy2lp18tc" />
                  <span className="text-[#FFD700] font-bold text-xs tracking-widest uppercase font-mono" data-id="element-9334" data-mp="10khag4ypfqno">
                    Pro Membership
                  </span>
                </div>
                <h2 className="text-3xl md:text-4xl font-black mb-6 text-white tracking-tight uppercase" data-id="element-9335" data-mp="rkari3k8lyh4">
                  Ready to get the{' '}
                  <span className="text-[#FF0000]" data-id="element-9336" data-mp="hnxx13gvbglb">full picture</span>?
                </h2>
                <p className="text-gray-400 mb-8 text-lg leading-relaxed font-mono" data-id="element-9337" data-mp="2h0tpzmfu5ekt">
                  Upgrade to Pro today and get immediate access to all current
                  and future premium tools, ensuring you never miss a critical
                  insight.
                </p>

                <ul className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-8 mb-8" data-id="element-9338" data-mp="2okbab20wm8if">
                  {benefits.map((benefit, index) => <li key={index} className="flex items-center text-gray-300" data-id="element-9339" data-mp="2tc2sao5zv5wy" data-mpi={index}>
                      <CheckCircle2 className="w-5 h-5 mr-3 text-[#FF0000] flex-shrink-0" data-id="element-9340" data-mp="2yanqyj5llhja" />
                      <span className="text-sm font-mono" data-id="element-9341" data-mp="21kdfb5fhda0i">{benefit}</span>
                    </li>)}
                </ul>
              </div>

              <div className="flex-shrink-0 w-full lg:w-auto" data-id="element-9342" data-mp="25lqjlb2xd55b">
                {!isPro && !loadingProStatus ? <motion.button whileHover={{
                scale: 1.02
              }} whileTap={{
                scale: 0.98
              }} onClick={() => setShowSubscriptionModal(true)} className="w-full lg:w-auto bg-[#FF0000] hover:bg-red-600 text-white font-bold py-4 px-8 shadow-[0_0_20px_rgba(255,0,0,0.4)] transition-all flex items-center justify-center group uppercase tracking-wider" data-id="element-9343" data-mp="2amwenfk254i0">
                    <Zap className="w-5 h-5 mr-2 fill-white group-hover:scale-110 transition-transform" data-id="element-9344" data-mp="pnszle26gesn" />
                    View plans
                  </motion.button> : <div className="bg-[#050505] border border-[#FFD700]/30 p-8 text-center min-w-[280px]" data-id="element-9345" data-mp="zl736d30593j">
                    <div className="inline-flex items-center justify-center w-14 h-14 bg-[#FFD700]/10 border border-[#FFD700] rounded-full mb-4 shadow-[0_0_15px_rgba(255,215,0,0.2)]" data-id="element-9346" data-mp="umvu44kj72md">
                      <CheckCircle2 className="w-7 h-7 text-[#FFD700]" data-id="element-9347" data-mp="13naf12tl2fpc" />
                    </div>
                    <h3 className="font-bold text-xl mb-2 text-white uppercase tracking-wide" data-id="element-9348" data-mp="3ad8k5rasz79u">
                      Pro Active
                    </h3>
                    <p className="text-gray-400 text-xs font-mono uppercase tracking-widest" data-id="element-9349" data-mp="35fynprozxvci">
                      Full Access Granted
                    </p>
                  </div>}
              </div>
            </div>
          </motion.div>
        </main>
      </div>

      <SubscriptionModal isOpen={showSubscriptionModal} onClose={() => setShowSubscriptionModal(false)} data-id="element-9350" data-mp="1c2zwk16jk9ib" />
    </div>;
}